import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { codeScanAPI } from '../../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import useDocumentTitle from '../../../hooks/useDocumentTitle';

interface ScanResult {
  id: number;
  path: string;
  scanned_at: string;
  scan_duration_seconds: number;
  total_files_scanned: number;
  server_name: string;
  target_label: string | null;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  info_count: number;
  tech_stack?: { primary_language?: string; framework?: string; frontend?: string; package_manager?: string };
  runtime_versions?: Record<string, string>;
  web_server_info?: { type?: string; version?: string };
}

interface Vulnerability {
  id: number;
  severity: string;
  category: string;
  package_name: string | null;
  installed_version: string | null;
  fixed_version: string | null;
  cve_id: string | null;
  title: string;
  description: string | null;
  file_path: string | null;
  line_number: number | null;
  recommendation: string | null;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high:     '#ea580c',
  medium:   '#d97706',
  low:      '#65a30d',
  info:     '#2563eb',
};

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info'];

const SeverityBadge: React.FC<{ severity: string }> = ({ severity }) => (
  <span
    className="badge text-white text-capitalize"
    style={{ backgroundColor: SEVERITY_COLORS[severity] || '#6c757d' }}
  >
    {severity}
  </span>
);

const CategoryBadge: React.FC<{ category: string }> = ({ category }) => {
  const colors: Record<string, string> = {
    dependency: 'bg-primary-subtle text-primary',
    secret:     'bg-danger-subtle text-danger',
    config:     'bg-warning-subtle text-warning',
    version:    'bg-secondary-subtle text-secondary',
  };
  return <span className={`badge ${colors[category] || 'bg-secondary-subtle text-secondary'} text-capitalize`}>{category}</span>;
};

const CodeScanResultDetailPage: React.FC = () => {
  useDocumentTitle('Scan Result Detail');
  const { id } = useParams<{ id: string }>();
  const [result, setResult] = useState<ScanResult | null>(null);
  const [vulns, setVulns] = useState<Vulnerability[]>([]);
  const [loading, setLoading] = useState(true);
  const [vulnsLoading, setVulnsLoading] = useState(false);
  const [severityFilter, setSeverityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVulns, setTotalVulns] = useState(0);

  const loadResult = useCallback(async () => {
    if (!id) return;
    try {
      const res = await codeScanAPI.getResult(parseInt(id));
      setResult(res.data.data?.result);
    } catch {
      toast.error('Failed to load scan result');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadVulns = useCallback(async () => {
    if (!id) return;
    setVulnsLoading(true);
    try {
      const res = await codeScanAPI.getVulnerabilities(parseInt(id), {
        severity: severityFilter || undefined,
        category: categoryFilter || undefined,
        search: searchFilter || undefined,
        page,
        limit: 50,
      });
      setVulns(res.data.data?.vulnerabilities || []);
      setTotalPages(res.data.data?.pagination?.pages || 1);
      setTotalVulns(res.data.data?.pagination?.total || 0);
    } catch {
      toast.error('Failed to load vulnerabilities');
    } finally {
      setVulnsLoading(false);
    }
  }, [id, severityFilter, categoryFilter, searchFilter, page]);

  useEffect(() => { loadResult(); }, [loadResult]);
  useEffect(() => { if (!loading) loadVulns(); }, [loadVulns, loading]);

  const handleFilterChange = () => {
    setPage(1);
  };

  const totalCount = result
    ? result.critical_count + result.high_count + result.medium_count + result.low_count + result.info_count
    : 0;

  return (
    <DashboardLayout>
      <div className="page-header">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb small mb-1">
              <li className="breadcrumb-item"><Link to="/security/code-scan">Code Scanner</Link></li>
              <li className="breadcrumb-item active">Scan Result</li>
            </ol>
          </nav>
          <h1 className="page-title">Scan Result Detail</h1>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : !result ? (
        <div className="alert alert-danger">Result not found.</div>
      ) : (
        <>
          {/* Summary Card */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-5">
                  <h6 className="text-muted small mb-1">Server / Path</h6>
                  <div className="fw-medium">{result.server_name}</div>
                  <code className="small text-muted">{result.target_label || result.path}</code>
                </div>
                <div className="col-md-3">
                  <h6 className="text-muted small mb-1">Tech Stack</h6>
                  <div className="d-flex flex-wrap gap-1">
                    {result.tech_stack?.primary_language && (
                      <span className="badge bg-primary-subtle text-primary text-capitalize">
                        {result.tech_stack.primary_language}
                      </span>
                    )}
                    {result.tech_stack?.framework && (
                      <span className="badge bg-secondary-subtle text-secondary text-capitalize">
                        {result.tech_stack.framework}
                      </span>
                    )}
                    {result.web_server_info?.type && (
                      <span className="badge bg-info-subtle text-info text-capitalize">
                        {result.web_server_info.type}
                        {result.web_server_info.version ? ` ${result.web_server_info.version}` : ''}
                      </span>
                    )}
                    {result.runtime_versions && Object.entries(result.runtime_versions).map(([rt, ver]) => (
                      <span key={rt} className="badge bg-warning-subtle text-warning">
                        {rt} {ver}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="col-md-2">
                  <h6 className="text-muted small mb-1">Scanned</h6>
                  <div className="small">{new Date(result.scanned_at).toLocaleString()}</div>
                  <div className="text-muted small">{result.total_files_scanned} files · {result.scan_duration_seconds}s</div>
                </div>
                <div className="col-md-2">
                  <h6 className="text-muted small mb-1">Findings</h6>
                  <div className="d-flex flex-wrap gap-1">
                    {SEVERITY_ORDER.map(sev => {
                      const count = (result as any)[`${sev}_count`] || 0;
                      return count > 0 ? (
                        <span key={sev} className="badge text-white" style={{ backgroundColor: SEVERITY_COLORS[sev] }}>
                          {count} {sev}
                        </span>
                      ) : null;
                    })}
                    {totalCount === 0 && <span className="badge bg-success">Clean</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Severity Bar */}
          {totalCount > 0 && (
            <div className="card mb-4">
              <div className="card-body py-2">
                <div className="d-flex" style={{ height: 16, borderRadius: 8, overflow: 'hidden' }}>
                  {SEVERITY_ORDER.map(sev => {
                    const count = (result as any)[`${sev}_count`] || 0;
                    if (!count) return null;
                    return (
                      <div
                        key={sev}
                        title={`${sev}: ${count}`}
                        style={{
                          width: `${(count / totalCount) * 100}%`,
                          backgroundColor: SEVERITY_COLORS[sev],
                        }}
                      />
                    );
                  })}
                </div>
                <div className="d-flex gap-3 mt-2">
                  {SEVERITY_ORDER.map(sev => {
                    const count = (result as any)[`${sev}_count`] || 0;
                    return (
                      <span key={sev} className="small">
                        <span className="me-1" style={{ color: SEVERITY_COLORS[sev] }}>●</span>
                        <span className="text-capitalize text-muted">{sev}</span>
                        <span className="fw-bold ms-1">{count}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Vulnerabilities */}
          <div className="card">
            <div className="card-header">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <h6 className="card-title mb-0 me-auto">
                  Vulnerabilities ({totalVulns})
                </h6>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={severityFilter}
                  onChange={e => { setSeverityFilter(e.target.value); handleFilterChange(); }}
                >
                  <option value="">All Severities</option>
                  {SEVERITY_ORDER.map(s => <option key={s} value={s} className="text-capitalize">{s}</option>)}
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto' }}
                  value={categoryFilter}
                  onChange={e => { setCategoryFilter(e.target.value); handleFilterChange(); }}
                >
                  <option value="">All Categories</option>
                  <option value="dependency">Dependency</option>
                  <option value="secret">Secret</option>
                  <option value="version">Version EOL</option>
                  <option value="config">Config</option>
                </select>
                <input
                  type="search"
                  className="form-control form-control-sm"
                  style={{ width: 160 }}
                  placeholder="Search..."
                  value={searchFilter}
                  onChange={e => { setSearchFilter(e.target.value); handleFilterChange(); }}
                />
              </div>
            </div>
            <div className="card-body p-0">
              {vulnsLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary" />
                </div>
              ) : vulns.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  {totalCount === 0
                    ? <><i className="bi bi-shield-check fs-2 text-success d-block mb-2" /><p>No vulnerabilities found. Great job!</p></>
                    : <p>No vulnerabilities match the current filters.</p>
                  }
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-top mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 90 }}>Severity</th>
                        <th style={{ width: 90 }}>Category</th>
                        <th>Title / Package</th>
                        <th>CVE</th>
                        <th>Fix</th>
                        <th>Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vulns.map(v => (
                        <tr key={v.id}>
                          <td><SeverityBadge severity={v.severity} /></td>
                          <td><CategoryBadge category={v.category} /></td>
                          <td>
                            <div className="fw-medium small">{v.title}</div>
                            {v.package_name && (
                              <div className="text-muted small">
                                {v.package_name}
                                {v.installed_version ? ` @ ${v.installed_version}` : ''}
                              </div>
                            )}
                            {v.description && (
                              <div className="text-muted small mt-1" style={{ maxWidth: 400 }}>
                                {v.description.length > 120 ? v.description.slice(0, 120) + '…' : v.description}
                              </div>
                            )}
                            {v.recommendation && (
                              <div className="text-success small mt-1">
                                <i className="bi bi-check-circle me-1" />
                                {v.recommendation}
                              </div>
                            )}
                          </td>
                          <td>
                            {v.cve_id ? (
                              <a
                                href={`https://nvd.nist.gov/vuln/detail/${v.cve_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="badge bg-danger-subtle text-danger text-decoration-none"
                              >
                                {v.cve_id}
                              </a>
                            ) : <span className="text-muted">—</span>}
                          </td>
                          <td>
                            {v.fixed_version ? (
                              <span className="badge bg-success-subtle text-success">{v.fixed_version}</span>
                            ) : <span className="text-muted">—</span>}
                          </td>
                          <td>
                            {v.file_path ? (
                              <code className="small text-muted">
                                {v.file_path}{v.line_number ? `:${v.line_number}` : ''}
                              </code>
                            ) : <span className="text-muted">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center py-3">
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => p - 1)}>‹</button>
                      </li>
                      {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
                        <li key={p} className={`page-item ${page === p ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                        </li>
                      ))}
                      <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(p => p + 1)}>›</button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
};

export default CodeScanResultDetailPage;
